import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fade,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BusinessIcon from "@mui/icons-material/Business";
import NotesIcon from "@mui/icons-material/Notes";
import { createDemoRequest } from "../../Services/DemoApi";
import { getPlaneForUser } from "../../Services/plansApi";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "24px",
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    overflow: "hidden",
  },
}));

const GradientHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  padding: theme.spacing(4),
  position: "relative",
  textAlign: "center",
  color: "white",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="25" cy="25" r="2" fill="%23ffffff" opacity="0.1"/><circle cx="75" cy="75" r="2" fill="%23ffffff" opacity="0.1"/></svg>\') repeat',
    opacity: 0.3,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
    },
    "&.Mui-focused": {
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.25)",
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
    },
    "&.Mui-focused": {
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.25)",
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "16px",
  padding: "12px 32px",
  fontSize: "1rem",
  fontWeight: "bold",
  textTransform: "none",
  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
  },
  "&:disabled": {
    background: "#e0e0e0",
  },
}));

const RequestDemoForm = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    tenant_name: "",
    plane_id: "",
    notes: "",
    broker_code: "", // حقل اختياري لكود الموزع
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // ✅ جلب الخطط عند فتح النافذة
  useEffect(() => {
    if (open) {
      setPlansLoading(true);
      getPlaneForUser()
        .then((res) => {
          setPlans(res.data?.data?.palnes || []); // ✅ التصحيح هنا
        })
        .catch((err) => {
          console.error("Error fetching plans:", err);
          setPlans([]);
        })
        .finally(() => setPlansLoading(false));
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setMessage({ type: "", text: "" });
  };

  const handleSubmit = async () => {
    if (!formData.tenant_name || !formData.plane_id) {
      setMessage({
        type: "error",
        text: "الرجاء إدخال اسم النسخة واختيار الخطة",
      });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const res = await createDemoRequest(formData);
      setMessage({
        type: "success",
        text: res.data?.message || "تم إرسال طلب النسخة التجريبية بنجاح ✅",
      });
      setTimeout(() => {
        setFormData({ tenant_name: "", plane_id: "", notes: "", broker_code: "" });
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error creating demo:", err);
      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          "حدث خطأ أثناء إرسال الطلب. الرجاء المحاولة لاحقًا.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <GradientHeader>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.3)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          sx={{
            width: 70,
            height: 70,
            mx: "auto",
            mb: 2,
            background: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 40, color: "white" }} />
        </Avatar>

        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            textShadow: "0 2px 10px rgba(0,0,0,0.1)",
            mb: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          طلب نسخة تجريبية
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.95, position: "relative", zIndex: 1 }}
        >
          ابدأ رحلتك مع نظامنا المتطور
        </Typography>
      </GradientHeader>

      <DialogContent sx={{ p: 4 }}>
        <Fade in={message.text !== ""} timeout={500}>
          <Box sx={{ mb: 3 }}>
            {message.text && (
              <Alert
                severity={message.type === "error" ? "error" : "success"}
                sx={{
                  borderRadius: "12px",
                  fontWeight: 500,
                  animation: "slideDown 0.3s ease",
                  "@keyframes slideDown": {
                    from: { opacity: 0, transform: "translateY(-10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                {message.text}
              </Alert>
            )}
          </Box>
        </Fade>

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BusinessIcon sx={{ color: "#667eea", mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            معلومات النسخة
          </Typography>
        </Box>

        <StyledTextField
          label="اسم النسخة (Tenant Name)"
          fullWidth
          margin="dense"
          value={formData.tenant_name}
          onChange={(e) => handleChange("tenant_name", e.target.value)}
          placeholder="أدخل اسم المطعم أو المشروع"
          sx={{ mb: 2 }}
        />

        {/* حقل اختياري: broker_code */}
        <StyledTextField
          label="كود الموزع (اختياري)"
          fullWidth
          margin="dense"
          value={formData.broker_code}
          onChange={(e) => handleChange("broker_code", e.target.value)}
          placeholder="ادخل كود الموزع إن وُجد"
          sx={{ mb: 2 }}
        />

        <StyledFormControl fullWidth margin="dense" sx={{ mb: 2 }}>
          <InputLabel id="plan-select-label">اختر الخطة</InputLabel>
          <Select
            labelId="plan-select-label"
            value={formData.plane_id}
            label="اختر الخطة"
            onChange={(e) => handleChange("plane_id", e.target.value)}
            disabled={plansLoading}
          >
            {plansLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                جارٍ تحميل الخطط...
              </MenuItem>
            ) : plans.length > 0 ? (
              plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.plane_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>لا توجد خطط متاحة</MenuItem>
            )}
          </Select>
        </StyledFormControl>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <NotesIcon sx={{ color: "#667eea", mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            ملاحظات إضافية
          </Typography>
        </Box>

        <StyledTextField
          label="ملاحظات (اختياري)"
          fullWidth
          margin="dense"
          multiline
          rows={4}
          value={formData.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="أي معلومات إضافية تود مشاركتها..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            borderRadius: "12px",
            px: 3,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          إلغاء
        </Button>
        <SubmitButton
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RocketLaunchIcon />
            )
          }
        >
          {loading ? "جاري الإرسال..." : "إرسال الطلب"}
        </SubmitButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RequestDemoForm;
